import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useParams } from 'react-router-dom';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { AccountingBook, AccountingBookListResponse } from '../../lib/types/finance.js';
import {
  ALL_BOOKS,
  isStatutoryBook,
  resolveBook,
  statutoryFeaturesAvailable,
} from './bookSelection.js';

export { ALL_BOOKS, isStatutoryBook };

/**
 * Which accounting book (دفتر قانونی / دفتر مدیریتی, D-022) the finance module
 * is looking at. One choice per tenant, remembered for the browser session and
 * shared by every finance page, so the book switcher in the module header,
 * the report pages and the tab strip always agree.
 *
 * Presentation only: every report endpoint takes `bookId` and the API checks
 * the caller's book permissions; this just decides what to ask for and show.
 */

const STORAGE_PREFIX = 'sk.finance.book:';
const listeners = new Set<() => void>();
let booksVersion = 0;

function read(slug: string | undefined): string | null {
  if (!slug) return null;
  try {
    return window.sessionStorage.getItem(STORAGE_PREFIX + slug);
  } catch {
    return null;
  }
}

function write(slug: string, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(STORAGE_PREFIX + slug);
    else window.sessionStorage.setItem(STORAGE_PREFIX + slug, value);
  } catch {
    // Storage can be unavailable (private window); the choice then lasts until reload.
  }
  for (const listener of listeners) listener();
}

/** Call after creating or changing a book so every mounted `useFinanceBook` reloads the list. */
export function refreshFinanceBooks(): void {
  booksVersion += 1;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export interface FinanceBookState {
  /** The book list has loaded. Pages that query by book wait for this. */
  readonly ready: boolean;
  readonly books: readonly AccountingBook[];
  /** The single book reports run against (the statutory book when «all» is chosen). */
  readonly book: AccountingBook | null;
  readonly bookId: string | null;
  /** «همه دفاتر» — only the journal list can show every book at once. */
  readonly isAll: boolean;
  /** False only while the management (non-statutory) book is the one chosen. */
  readonly statutoryFeaturesAvailable: boolean;
  readonly select: (bookIdOrAll: string) => void;
}

export function useFinanceBook(): FinanceBookState {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const version = useSyncExternalStore(
    subscribe,
    () => booksVersion,
    () => 0,
  );
  const booksRes = useApiGet<AccountingBookListResponse>(
    slug ? `${base}/api/v1/finance/books` : null,
    [slug, version],
  );
  const stored = useSyncExternalStore(
    subscribe,
    () => read(slug),
    () => null,
  );

  const books = useMemo<readonly AccountingBook[]>(
    () =>
      booksRes.status === 'ready' ? booksRes.data.items.filter((b) => b.status !== 'archived') : [],
    [booksRes],
  );
  const ready = booksRes.status === 'ready' || booksRes.status === 'error';

  const { book, isAll } = useMemo(() => resolveBook(books, stored), [books, stored]);

  const select = useCallback(
    (bookIdOrAll: string) => {
      if (slug) write(slug, bookIdOrAll);
    },
    [slug],
  );

  return {
    ready,
    books,
    book,
    bookId: book?.bookId ?? null,
    isAll,
    statutoryFeaturesAvailable: statutoryFeaturesAvailable(book, isAll),
    select,
  };
}

/** `&bookId=…` / `?bookId=…` for a report URL, or '' until the book is known. */
export function bookQuery(state: FinanceBookState): string {
  return state.bookId ? `bookId=${encodeURIComponent(state.bookId)}` : '';
}
